import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Navbar } from "../../components/Navbar";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import {
  FileText,
  Plus,
  Search,
  RefreshCw,
  Loader2,
  Edit,
  Trash2,
  Eye,
  Ship,
  Package,
  FileCheck,
  FilePlus,
  History,
  Download,
  Upload,
  Copy,
  Printer,
  Users,
  X,
} from "lucide-react";
import { ContactAutocomplete } from "../../components/ContactAutocomplete";
import { API_URL, API_BASE_URL } from "../../config/api";


// Empty cargo line template
const emptyCargo = { marks_nos: "", packages: "", description: "", gross_weight: "", measurement: "" };

export default function BillOfLadingPage() {
  const [activeTab, setActiveTab] = useState("all");
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [showRevisions, setShowRevisions] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [saving, setSaving] = useState(false);
  const { getAuthHeaders } = useAuth();
  
  // Pagination and sorting state
  const [totalCount, setTotalCount] = useState(0);
  const [currentSkip, setCurrentSkip] = useState(0);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const PAGE_SIZE = 50;

  // Form state
  const [form, setForm] = useState({
    status: "draft",
    bl_type: "hbl",
    pf_number: "",
    document_number: "",
    bl_number: "",
    reference_nos: "",
    shipper_exporter: "",
    consignee: "",
    notify_party: "",
    forwarding_agent: "",
    delivery_apply_to: "",
    vessel_voyage: "",
    place_of_receipt: "",
    port_of_loading: "",
    port_of_discharge: "",
    place_of_delivery: "",
    point_of_origin_ftz: "",
    type_of_move: "",
    cargo_lines: Array(12).fill(null).map(() => ({ ...emptyCargo })),
    carriers_receipt: "",
    freight_charges_payable_at: "",
    freight_details: "",
    prepaid: "",
    collect: "",
    declared_cargo_value: "",
    number_of_original_bl: "",
    place_date_of_issue: "",
    signed_issued_by: "",
    total_charges: "",
    total_packages: "",
    notes: "",
  });

  const fetchDocuments = async (status = null, append = false) => {
    try {
      const skip = append ? currentSkip + PAGE_SIZE : 0;
      const params = { 
        limit: PAGE_SIZE,
        skip: skip,
        sort_by: sortBy,
        sort_order: sortOrder
      };
      if (status && status !== "all") params.status = status;
      if (searchQuery) params.search = searchQuery;
      
      const response = await axios.get(`${API_URL}/bl/documents`, { 
        headers: getAuthHeaders(),
        params 
      });
      
      if (append) {
        setDocuments(prev => [...prev, ...response.data.documents]);
      } else {
        setDocuments(response.data.documents);
      }
      setTotalCount(response.data.total);
      setCurrentSkip(skip);
    } catch (error) {
      console.error("Failed to fetch documents:", error);
    }
  };
  
  const loadMore = async () => {
    setLoadingMore(true);
    await fetchDocuments(activeTab, true);
    setLoadingMore(false);
  };
  
  const handleSortChange = (field) => {
    if (sortBy === field) {
      // Toggle sort order
      setSortOrder(prev => prev === "desc" ? "asc" : "desc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/bl/stats`, { headers: getAuthHeaders() });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    await Promise.all([fetchStats(), fetchDocuments(activeTab)]);
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setCurrentSkip(0);
    fetchDocuments(activeTab);
  }, [activeTab, searchQuery, sortBy, sortOrder]);

  const resetForm = () => {
    setForm({
      status: "draft",
      bl_type: "hbl",
      pf_number: "",
      document_number: "",
      bl_number: "",
      reference_nos: "",
      shipper_exporter: "",
      consignee: "",
      notify_party: "",
      forwarding_agent: "",
      delivery_apply_to: "",
      vessel_voyage: "",
      place_of_receipt: "",
      port_of_loading: "",
      port_of_discharge: "",
      place_of_delivery: "",
      point_of_origin_ftz: "",
      type_of_move: "",
      cargo_lines: Array(12).fill(null).map(() => ({ ...emptyCargo })),
      carriers_receipt: "",
      freight_charges_payable_at: "",
      freight_details: "",
      prepaid: "",
      collect: "",
      declared_cargo_value: "",
      number_of_original_bl: "",
      place_date_of_issue: "",
      signed_issued_by: "",
      total_charges: "",
      total_packages: "",
      notes: "",
    });
    setEditingDoc(null);
  };

  const handleCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = async (doc) => {
    try {
      // Fetch full document data with converted cargo_lines
      const response = await axios.get(`${API_URL}/bl/documents/${doc.id}`, { headers: getAuthHeaders() });
      const fullDoc = response.data;
      
      setEditingDoc(fullDoc);
      // Ensure cargo_lines has 12 rows
      const cargoLines = fullDoc.cargo_lines || [];
      while (cargoLines.length < 12) {
        cargoLines.push({ ...emptyCargo });
      }
      setForm({
        ...fullDoc,
        cargo_lines: cargoLines,
      });
      setShowForm(true);
    } catch (error) {
      console.error("Failed to fetch BL details:", error);
      // Fallback to using doc from list
      setEditingDoc(doc);
      const cargoLines = doc.cargo_lines || [];
      while (cargoLines.length < 12) {
        cargoLines.push({ ...emptyCargo });
      }
      setForm({
        ...doc,
        cargo_lines: cargoLines,
      });
      setShowForm(true);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Filter out empty cargo lines for saving
      const dataToSave = {
        ...form,
        cargo_lines: form.cargo_lines.filter(line => 
          line.marks_nos || line.packages || line.description || line.gross_weight || line.measurement
        )
      };

      if (editingDoc) {
        await axios.put(`${API_URL}/bl/documents/${editingDoc.id}`, dataToSave, { headers: getAuthHeaders() });
        toast.success("BL updated successfully");
      } else {
        await axios.post(`${API_URL}/bl/documents`, dataToSave, { headers: getAuthHeaders() });
        toast.success("BL created successfully");
      }
      setShowForm(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save BL");
    } finally {
      setSaving(false);
    }
  };

  const handleClone = async (doc) => {
    try {
      // Fetch full document data with converted cargo_lines
      const response = await axios.get(`${API_URL}/bl/documents/${doc.id}`, { headers: getAuthHeaders() });
      const fullDoc = response.data;
      
      // Clone the document but clear ID and numbers for new generation
      const cargoLines = fullDoc.cargo_lines || [];
      while (cargoLines.length < 12) {
        cargoLines.push({ ...emptyCargo });
      }
      
      setEditingDoc(null); // Mark as new
      setForm({
        ...fullDoc,
        status: "draft",
        document_number: "", // Will be auto-generated
        bl_number: "", // Will be auto-generated
        cargo_lines: cargoLines,
      });
      setShowForm(true);
      toast.info("Cloned BL - modify and save as new");
    } catch (error) {
      // Fallback
      const cargoLines = doc.cargo_lines || [];
      while (cargoLines.length < 12) {
        cargoLines.push({ ...emptyCargo });
      }
      
      setEditingDoc(null);
      setForm({
        ...doc,
        status: "draft",
        document_number: "",
        bl_number: "",
        cargo_lines: cargoLines,
      });
      setShowForm(true);
      toast.info("Cloned BL - modify and save as new");
    }
  };

  const handlePrint = async (doc) => {
    const isSWB = doc.bl_type && doc.bl_type.toLowerCase() === 'swb';
    const isMBL = doc.bl_type && doc.bl_type.toLowerCase() === 'mbl';
    const needsBackground = isSWB || isMBL;
    const printMode = needsBackground ? 'full' : 'text';
    toast.info(isSWB ? "Generating Sea Waybill PDF..." : isMBL ? "Generating Master BL PDF..." : "Generating PDF for printing...");
    try {
      const response = await axios.get(
        `${API_URL}/bl/documents/${doc.id}/print?mode=${printMode}`,
        { 
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      // Create download link
      const prefix = isSWB ? 'SWB' : isMBL ? 'MBL' : 'BL';
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${prefix}_${doc.bl_number.replace(/\//g, '-')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(isSWB ? "Sea Waybill PDF downloaded" : isMBL ? "Master BL PDF downloaded" : "PDF downloaded - print on pre-printed BL forms");
    } catch (error) {
      toast.error("Failed to generate PDF");
      console.error(error);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete BL ${doc.bl_number}?`)) return;
    try {
      await axios.delete(`${API_URL}/bl/documents/${doc.id}`, { headers: getAuthHeaders() });
      toast.success("BL deleted");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  const viewRevisions = async (doc) => {
    try {
      const response = await axios.get(`${API_URL}/bl/documents/${doc.id}/revisions`, { headers: getAuthHeaders() });
      setRevisions(response.data.revisions || []);
      setShowRevisions(true);
    } catch (error) {
      toast.error("Failed to load revisions");
    }
  };

  const updateCargoLine = (index, field, value) => {
    const newLines = [...form.cargo_lines];
    newLines[index] = { ...newLines[index], [field]: value };
    setForm({ ...form, cargo_lines: newLines });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-20 pb-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 flex items-center justify-between"
          >
            <div>
              <h1 className="font-heading text-3xl font-bold text-foreground">
                <span className="text-primary">BL</span> Bill of Lading
              </h1>
              <p className="text-muted-foreground text-sm">
                Manage House Bills of Lading and Sea Waybills
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={loadData} data-testid="refresh-bl-btn">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button onClick={handleCreate} data-testid="add-bl-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add New
              </Button>
            </div>
          </motion.div>

          {/* Stats Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6"
          >
            <div className="card-surface p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded bg-primary/10 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats?.total || 0}</p>
                  <p className="text-xs text-muted-foreground">Total BLs</p>
                </div>
              </div>
            </div>
            <div className="card-surface p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded bg-warning/10 flex items-center justify-center">
                  <FilePlus className="h-5 w-5 text-warning" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats?.drafts || 0}</p>
                  <p className="text-xs text-muted-foreground">Drafts</p>
                </div>
              </div>
            </div>
            <div className="card-surface p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded bg-success/10 flex items-center justify-center">
                  <FileCheck className="h-5 w-5 text-success" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats?.originals || 0}</p>
                  <p className="text-xs text-muted-foreground">Originals</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Tabs and Search */}
          <div className="card-surface p-4 mb-4">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="bg-background border border-border">
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="draft" className="data-[state=active]:bg-warning/20">Draft</TabsTrigger>
                  <TabsTrigger value="original" className="data-[state=active]:bg-success/20">Original</TabsTrigger>
                </TabsList>
              </Tabs>
              
              {/* Sort controls */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Sort by:</span>
                <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setCurrentSkip(0); }}>
                  <SelectTrigger className="w-[140px] h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="created_at">Date Created</SelectItem>
                    <SelectItem value="updated_at">Date Updated</SelectItem>
                    <SelectItem value="bl_number">BL Number</SelectItem>
                  </SelectContent>
                </Select>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="h-8 px-2"
                  onClick={() => setSortOrder(prev => prev === "desc" ? "asc" : "desc")}
                >
                  {sortOrder === "desc" ? "↓ Newest" : "↑ Oldest"}
                </Button>
              </div>
              
              <div className="relative w-full md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search BL, shipper, vessel..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-bl-input"
                />
              </div>
            </div>
            
            {/* Results count */}
            <div className="mt-3 text-xs text-muted-foreground">
              Showing {documents.length} of {totalCount} documents
            </div>
          </div>

          {/* Documents Table */}
          <div className="card-surface overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="w-[140px]">BL Number</TableHead>
                  <TableHead>Rev</TableHead>
                  <TableHead className="min-w-[200px]">Shipper</TableHead>
                  <TableHead className="min-w-[200px]">Consignee</TableHead>
                  <TableHead>Notify Party</TableHead>
                  <TableHead>Vessel</TableHead>
                  <TableHead>Creator</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((doc) => (
                  <TableRow key={doc.id} className="hover:bg-muted/20">
                    <TableCell>
                      <button
                        onClick={() => handleEdit(doc)}
                        className="text-primary hover:underline font-medium text-sm"
                      >
                        {doc.bl_number}
                      </button>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-xs bg-muted px-2 py-0.5 rounded">{doc.revision_count || 1}</span>
                    </TableCell>
                    <TableCell className="text-xs max-w-[200px] truncate" title={doc.shipper_exporter}>
                      {doc.shipper_exporter?.substring(0, 50)}
                    </TableCell>
                    <TableCell className="text-xs max-w-[200px] truncate" title={doc.consignee}>
                      {doc.consignee?.substring(0, 50)}
                    </TableCell>
                    <TableCell className="text-xs truncate">{doc.notify_party?.substring(0, 30)}</TableCell>
                    <TableCell className="text-xs">{doc.vessel_voyage?.substring(0, 20)}</TableCell>
                    <TableCell className="text-xs">{doc.created_by?.split("@")[0]}</TableCell>
                    <TableCell className="text-xs">
                      {new Date(doc.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => handleEdit(doc)} title="Edit">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => handlePrint(doc)} title="Print PDF">
                          <Printer className="h-4 w-4 text-success" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => handleClone(doc)} title="Clone">
                          <Copy className="h-4 w-4 text-primary" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => viewRevisions(doc)} title="History">
                          <History className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => handleDelete(doc)} title="Delete">
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {documents.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      No BL documents found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            
            {/* Load More Button */}
            {documents.length < totalCount && (
              <div className="p-4 border-t flex justify-center">
                <Button 
                  variant="outline" 
                  onClick={loadMore} 
                  disabled={loadingMore}
                  className="gap-2"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4" />
                      Load More ({totalCount - documents.length} remaining)
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* BL Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ship className="h-5 w-5 text-primary" />
              {editingDoc ? `Edit BL: ${editingDoc.bl_number}` : "New Bill of Lading"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Status & Type Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg">
              <div>
                <Label>Draft or Confirmed *</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="bl-status-toggle">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="original">Confirmed</SelectItem>
                  </SelectContent>
                </Select>
                {form.status === "draft" ? (
                  <p className="text-[11px] text-amber-600 mt-1">
                    PDF will include a <strong>DRAFT</strong> watermark.
                  </p>
                ) : (
                  <p className="text-[11px] text-emerald-600 mt-1">
                    PDF will be issued <strong>clean</strong> (no watermark).
                  </p>
                )}
              </div>
              <div>
                <Label>BL Type</Label>
                <Select value={form.bl_type} onValueChange={(v) => setForm({ ...form, bl_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hbl">House BL</SelectItem>
                    <SelectItem value="swb">Sea Waybill</SelectItem>
                    <SelectItem value="mbl">Master BL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>(4) Document Number</Label>
                <Input
                  value={form.document_number}
                  onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                  placeholder="Auto-generated"
                />
              </div>
              <div>
                <Label>(5) BL Number</Label>
                <Input
                  value={form.bl_number}
                  onChange={(e) => setForm({ ...form, bl_number: e.target.value })}
                  placeholder="Auto: SIKOP/PF.../YY"
                />
              </div>
            </div>

            {/* Row: Reference & PF */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>(6) Reference Nos</Label>
                <Input
                  value={form.reference_nos}
                  onChange={(e) => setForm({ ...form, reference_nos: e.target.value })}
                />
              </div>
              <div>
                <Label>PF Number (Link)</Label>
                <Input
                  value={form.pf_number}
                  onChange={(e) => setForm({ ...form, pf_number: e.target.value })}
                  placeholder="e.g., PF000084"
                />
              </div>
            </div>

            {/* CRM Quick Fill for Parties */}
            <div className="bg-muted/30 rounded-lg p-3 border border-border/50">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Quick Fill from CRM</span>
              </div>
              <ContactAutocomplete
                type="both"
                placeholder="Search contacts or companies to auto-fill..."
                onSelect={(item) => {
                  if (item) {
                    if (item._type === "company") {
                      // For company, build address string
                      const addressParts = [item.name];
                      if (item.address) addressParts.push(item.address);
                      if (item.country) addressParts.push(item.country);
                      const fullAddress = addressParts.join("\n");
                      
                      // Ask which field to fill
                      const choice = window.confirm(
                        `Fill "${item.name}" as:\n\nOK = Shipper\nCancel = Consignee`
                      );
                      if (choice) {
                        setForm(prev => ({ ...prev, shipper_exporter: fullAddress }));
                        toast.success(`Shipper filled with ${item.name}`);
                      } else {
                        setForm(prev => ({ ...prev, consignee: fullAddress }));
                        toast.success(`Consignee filled with ${item.name}`);
                      }
                    } else {
                      // For contact, include company if available
                      const addressParts = [item.name];
                      if (item.company_name) addressParts.push(item.company_name);
                      if (item.email) addressParts.push(item.email);
                      const fullAddress = addressParts.join("\n");
                      
                      const choice = window.confirm(
                        `Fill "${item.name}" as:\n\nOK = Shipper\nCancel = Consignee`
                      );
                      if (choice) {
                        setForm(prev => ({ ...prev, shipper_exporter: fullAddress }));
                        toast.success(`Shipper filled with ${item.name}`);
                      } else {
                        setForm(prev => ({ ...prev, consignee: fullAddress }));
                        toast.success(`Consignee filled with ${item.name}`);
                      }
                    }
                  }
                }}
              />
            </div>

            {/* Parties Section */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>(1) Shipper / Exporter</Label>
                <Textarea
                  value={form.shipper_exporter}
                  onChange={(e) => setForm({ ...form, shipper_exporter: e.target.value })}
                  rows={3}
                  className="text-sm"
                  data-testid="bl-shipper-input"
                />
              </div>
              <div>
                <Label>(7) Forwarding Agent</Label>
                <Textarea
                  value={form.forwarding_agent}
                  onChange={(e) => setForm({ ...form, forwarding_agent: e.target.value })}
                  rows={3}
                  className="text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>(2) Consignee</Label>
                <Textarea
                  value={form.consignee}
                  onChange={(e) => setForm({ ...form, consignee: e.target.value })}
                  rows={3}
                  className="text-sm"
                  placeholder='(This bill is non-negotiable unless marked "To order" or "To the order of ...")'
                  data-testid="bl-consignee-input"
                />
              </div>
              <div>
                <Label>(8) For Delivery of the Goods Apply To</Label>
                <Textarea
                  value={form.delivery_apply_to}
                  onChange={(e) => setForm({ ...form, delivery_apply_to: e.target.value })}
                  rows={3}
                  className="text-sm"
                />
              </div>
            </div>

            <div>
              <Label>(3) Notify Party</Label>
              <Textarea
                value={form.notify_party}
                onChange={(e) => setForm({ ...form, notify_party: e.target.value })}
                rows={2}
                className="text-sm"
                data-testid="bl-notify-party-input"
              />
            </div>

            {/* Vessel & Ports */}
            <div>
              <Label>(9) Vessel / Voyage</Label>
              <Input
                value={form.vessel_voyage}
                onChange={(e) => setForm({ ...form, vessel_voyage: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>(10) Place of Receipt</Label>
                <Input
                  value={form.place_of_receipt}
                  onChange={(e) => setForm({ ...form, place_of_receipt: e.target.value })}
                  placeholder="(Combined transport only)"
                />
              </div>
              <div>
                <Label>(11) Port of Loading</Label>
                <Input
                  value={form.port_of_loading}
                  onChange={(e) => setForm({ ...form, port_of_loading: e.target.value })}
                />
              </div>
              <div>
                <Label>(14) Point (State) of Origin or FTZ Number</Label>
                <Input
                  value={form.point_of_origin_ftz}
                  onChange={(e) => setForm({ ...form, point_of_origin_ftz: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>(12) Port of Discharge</Label>
                <Input
                  value={form.port_of_discharge}
                  onChange={(e) => setForm({ ...form, port_of_discharge: e.target.value })}
                />
              </div>
              <div>
                <Label>(13) Place of Delivery</Label>
                <Input
                  value={form.place_of_delivery}
                  onChange={(e) => setForm({ ...form, place_of_delivery: e.target.value })}
                  placeholder="(Combined transport only)"
                />
              </div>
              <div>
                <Label>(15) Type of Move</Label>
                <Input
                  value={form.type_of_move}
                  onChange={(e) => setForm({ ...form, type_of_move: e.target.value })}
                />
              </div>
            </div>

            {/* Cargo Details Section */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-muted/50 px-3 py-2 text-sm font-medium border-b">
                (16) Below Particulars Furnished by Shipper - Carrier Not Responsible
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/30 border-b">
                      <th className="px-2 py-2 text-left w-[150px]">(17) Mks & Nos. CTR/Seal</th>
                      <th className="px-2 py-2 text-left w-[100px]">(18) Packages</th>
                      <th className="px-2 py-2 text-left">(19) Description of Packages & Goods</th>
                      <th className="px-2 py-2 text-left w-[100px]">(20) Gross Weight</th>
                      <th className="px-2 py-2 text-left w-[100px]">(21) Measurement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.cargo_lines.map((line, idx) => (
                      <tr key={idx} className="border-b">
                        <td className="p-1">
                          <Input
                            value={line.marks_nos}
                            onChange={(e) => updateCargoLine(idx, "marks_nos", e.target.value)}
                            className="h-8 text-xs"
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            value={line.packages}
                            onChange={(e) => updateCargoLine(idx, "packages", e.target.value)}
                            className="h-8 text-xs"
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            value={line.description}
                            onChange={(e) => updateCargoLine(idx, "description", e.target.value)}
                            className="h-8 text-xs"
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            value={line.gross_weight}
                            onChange={(e) => updateCargoLine(idx, "gross_weight", e.target.value)}
                            className="h-8 text-xs"
                          />
                        </td>
                        <td className="p-1">
                          <Input
                            value={line.measurement}
                            onChange={(e) => updateCargoLine(idx, "measurement", e.target.value)}
                            className="h-8 text-xs"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Carrier Receipt & Freight */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>(22) Carrier's Receipt</Label>
                <Textarea
                  value={form.carriers_receipt}
                  onChange={(e) => setForm({ ...form, carriers_receipt: e.target.value })}
                  rows={2}
                  className="text-sm"
                  placeholder="Total number of containers or packages received by Carrier"
                />
              </div>
              <div>
                <Label>(23) Freight Charges Payable At</Label>
                <Input
                  value={form.freight_charges_payable_at}
                  onChange={(e) => setForm({ ...form, freight_charges_payable_at: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>(24) Freight Details, Charges, etc.</Label>
                <Textarea
                  value={form.freight_details}
                  onChange={(e) => setForm({ ...form, freight_details: e.target.value })}
                  rows={2}
                  className="text-sm"
                />
              </div>
              <div>
                <Label>(25) Prepaid</Label>
                <Textarea
                  value={form.prepaid}
                  onChange={(e) => setForm({ ...form, prepaid: e.target.value })}
                  rows={2}
                  className="text-sm"
                />
              </div>
              <div>
                <Label>(26) Collect</Label>
                <Textarea
                  value={form.collect}
                  onChange={(e) => setForm({ ...form, collect: e.target.value })}
                  rows={2}
                  className="text-sm"
                />
              </div>
            </div>

            {/* Declaration & Issue */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>(27) Declared Cargo Value</Label>
                <Input
                  value={form.declared_cargo_value}
                  onChange={(e) => setForm({ ...form, declared_cargo_value: e.target.value })}
                />
              </div>
              <div>
                <Label>(28) Number of Original B/L</Label>
                <Input
                  value={form.number_of_original_bl}
                  onChange={(e) => setForm({ ...form, number_of_original_bl: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>(29) Place and Date of Issue of B/L</Label>
                <Input
                  value={form.place_date_of_issue}
                  onChange={(e) => setForm({ ...form, place_date_of_issue: e.target.value })}
                />
              </div>
              <div>
                <Label>(30) Signed and Issued By</Label>
                <Input
                  value={form.signed_issued_by}
                  onChange={(e) => setForm({ ...form, signed_issued_by: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>(31) Total Charges</Label>
                <Input
                  value={form.total_charges}
                  onChange={(e) => setForm({ ...form, total_charges: e.target.value })}
                />
              </div>
              <div>
                <Label>(32) Total No. of Packages</Label>
                <Input
                  value={form.total_packages}
                  onChange={(e) => setForm({ ...form, total_packages: e.target.value })}
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label>Internal Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                placeholder="Notes (not printed on BL)"
              />
            </div>
          </div>

          {/* Sticky Footer with Save/Cancel buttons */}
          <div className="sticky bottom-0 bg-background border-t py-4 px-2 -mx-2 flex justify-between gap-2 mt-6">
            <div>
              {editingDoc && (
                <Button variant="outline" onClick={() => handlePrint(editingDoc)} className="text-green-600 border-green-600 hover:bg-green-50">
                  <Printer className="h-4 w-4 mr-2" />
                  Print PDF
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-bl-btn" className="bg-primary hover:bg-primary/90">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileCheck className="h-4 w-4 mr-2" />}
                Save & Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Revisions Dialog */}
      <Dialog open={showRevisions} onOpenChange={setShowRevisions}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Revision History
            </DialogTitle>
          </DialogHeader>
          {revisions.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">No revisions yet</p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {revisions.map((rev, idx) => (
                <div key={idx} className="p-3 bg-muted/30 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Revision {rev.revision_number}</span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(rev.changed_at).toLocaleString()} by {rev.changed_by}
                    </span>
                  </div>
                  {rev.reason && (
                    <p className="text-sm text-muted-foreground mb-2">Reason: {rev.reason}</p>
                  )}
                  <div className="space-y-1">
                    {Object.entries(rev.changes).map(([field, change]) => (
                      <div key={field} className="text-xs">
                        <span className="font-medium">{field}:</span>{" "}
                        <span className="text-destructive line-through">{change.old || "(empty)"}</span>
                        {" → "}
                        <span className="text-success">{change.new || "(empty)"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
